import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { useEmployeeContext } from "../../context/EmployeeCtx";
import StepFooter from "../StepFooter";
import AddOption from "../steps/AddOption";
import DeclarationTrainings from "../steps/declaration-trainings";

import FormDateInput from "@/components/form-fields/form-date-picker";
import FormInput from "@/components/form-fields/form-input";
import {
  FormSelectSingle,
  SelectMulti,
} from "@/components/reusables/custom-form-fields/FormSelect";
import { Form } from "@/components/ui/form";
import { tempDesiredRolesOptions } from "@/constants/constant-values/temp-data.constant";
import {
  EmployeeSuccessMessage,
  EmploymentTypeList,
  OptionToast,
} from "@/constants/employees/employee.constants";
import { EmployeeOptionsType } from "@/interfaces/globals";
import {
  employeeApi,
  useGetEmployeeOptionsQuery,
  useUpdateEmployeeOptionsMutation,
  useUpdateWorkInfoMutation,
} from "@/redux/api/employeeQuery";
import {
  TemployeeWorkInfoSchema,
  employeeWorkInfoSchema,
} from "@/schemas/employee-schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type WorkFormProps = {
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  handleExit: () => void;
  operationType: "edit" | "add";
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
};

const WorkForm = ({
  setActiveTab,
  handleExit,
  setIsFormDirty,
  operationType,
}: WorkFormProps): React.JSX.Element => {
  const { employeeId, workFormInfo } = useEmployeeContext();

  const form = useForm<TemployeeWorkInfoSchema>({
    resolver: zodResolver(employeeWorkInfoSchema),
    mode: "all",
    ...(workFormInfo &&
      employeeId && {
        values: workFormInfo,
      }),
  });

  const { data: typeList } = useGetEmployeeOptionsQuery({
    filters: "",
  });
  const [addOption, setAddOption] = useState<{
    isOpen: boolean;
    type: EmployeeOptionsType;
  }>({
    isOpen: false,
    type: EmployeeOptionsType.SKILLS,
  });

  const dispatch = useDispatch();
  const [deleteOptions] = useUpdateEmployeeOptionsMutation();
  const [updateWorkInfo, { isLoading, isSuccess }] =
    useUpdateWorkInfoMutation();

  const deleteOptionType = async (id: string): Promise<void> => {
    const res = await deleteOptions({ id, method: "DELETE" });
    if (res?.data && "data" in res && "message" in res?.data) {
      toast.success(OptionToast.DELETED);
    }
  };

  const onSubmit = async (values: TemployeeWorkInfoSchema): Promise<void> => {
    const serializedTrainings = values.trainings.map((t, index) => {
      const url = { ticketUrl: t.image?.[0] };

      return {
        ...url,
        name: values.previousExperience.trainings?.[index].label,
        expiryDate: t.expiryDate,
        issuedDate: t.issuedDate,
      };
    });
    const formData = new FormData();
    formData.append(
      "employmentSeeking",
      JSON.stringify(values.employmentSeeking),
    );
    formData.append(
      "previousExperience",
      JSON.stringify({
        skills: values.previousExperience.skills,
        referenceDetails: values.previousExperience.referenceDetails,
        trainings: serializedTrainings,
      }),
    );
    values.trainings.forEach((t, index) => {
      if (Array.isArray(t.image)) {
        return formData.append(`tickets[${index}].image`, t.image?.[0] ?? "");
      }

      return null;
    });

    const response = await updateWorkInfo({
      employeeId: employeeId ?? "",
      data: formData,
    });
    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(EmployeeSuccessMessage.UPDATED);
        dispatch(employeeApi.util.invalidateTags(["employee"]));
        handleExit();
      },
    });
  };

  const {
    formState: { dirtyFields },
  } = form;
  useEffect(() => {
    if (dirtyFields && Object.keys(dirtyFields).length > 0) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  }, [JSON.stringify(dirtyFields)]);

  const { fields: selectedTrainings, remove } = useFieldArray({
    control: form.control,
    name: "previousExperience.trainings",
  });

  const { fields: trainings, remove: removeTrainings } = useFieldArray({
    control: form.control,
    name: "trainings",
  });

  const handleRemovePrevExpTrainings = (index: number): void => {
    remove(index);
    removeTrainings(index);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <h2 className='font-medium'>2. Type of Employment Seeking</h2>
        <section className='grid grid-cols-1 gap-4 mt-4 text-xs lg:grid-cols-3'>
          <FormInput
            form={form}
            name='employmentSeeking.employmentType'
            label='Employment Type'
            required
            render={(field) => (
              <FormSelectSingle
                field={field}
                options={EmploymentTypeList}
                placeholder='Select Employment Type'
              />
            )}
          />
          <FormInput
            form={form}
            name='employmentSeeking.desiredRole'
            label='Desired Role'
            required
            render={(field) => (
              <FormSelectSingle
                field={field}
                options={tempDesiredRolesOptions}
                placeholder='Select Desired Role'
              />
            )}
          />
          <FormInput
            form={form}
            name='employmentSeeking.proposedStartDate'
            label='Proposed Start Date'
            required
            render={(field) => (
              <FormDateInput
                type='date'
                defaultValue={field.value as unknown as Date | undefined}
                onChange={(date) => field.onChange(date)}
                dateLimit={new Date(new Date().getTime() - 24 * 60 * 60 * 1000)}
                placeholder='D/M/YYYY H:MM M'
                className='!w-full'
                isDateTime
              />
            )}
          />
        </section>
        <div className='mt-6 space-y-6'>
          <h2 className='font-medium'>3. Previous Experience:</h2>
          <section className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            <SelectMulti
              control={form.control}
              defaultValue={form.watch("previousExperience.skills")}
              name='previousExperience.skills'
              isRequired
              label='Skills'
              options={
                typeList
                  ?.filter((item) => item?.for === EmployeeOptionsType.SKILLS)
                  .map((item) => ({
                    label: item?.name || "",
                    value: item?.id || "",
                  })) || []
              }
              addButton={{
                label: "Add Skill",
                handleClick: () =>
                  setAddOption({
                    type: EmployeeOptionsType.SKILLS,
                    isOpen: true,
                  }),
              }}
              deleteAction={deleteOptionType}
              maxCount={2}
            />
            <SelectMulti
              withoutForm={{
                values:
                  form
                    .watch("previousExperience.trainings")
                    ?.map((item) => item?.value) || [],
                onValueChange: (value) => {
                  form.setValue("previousExperience.trainings", value, {
                    shouldDirty: true,
                  });
                },
              }}
              label='Trainings / Tickets'
              options={
                typeList
                  ?.filter(
                    (item) => item?.for === EmployeeOptionsType.TRAININGS,
                  )
                  .map((item) => ({
                    label: item?.name || "",
                    value: item?.name || "",
                    id: item?.id || "",
                  })) || []
              }
              addButton={{
                label: "Add Training or Ticket",
                handleClick: () =>
                  setAddOption({
                    type: EmployeeOptionsType.TRAININGS,
                    isOpen: true,
                  }),
              }}
              deleteAction={deleteOptionType}
              maxCount={2}
            />
          </section>
          <DeclarationTrainings
            selectedTrainings={selectedTrainings}
            handleRemoveTraining={handleRemovePrevExpTrainings}
            trainings={trainings}
          />
        </div>

        <StepFooter
          step={2}
          {...{ isLoading, isSuccess }}
          setActiveTab={setActiveTab}
          isActive={!Object.keys(form.formState.dirtyFields).length}
          operationType={operationType}
          reset={form.reset}
        />
        {addOption && (
          <AddOption
            isOpen={addOption.isOpen}
            toggleModal={() =>
              setAddOption((prev) => ({ ...prev, isOpen: !prev.isOpen }))
            }
            type={addOption.type}
          />
        )}
      </form>
    </Form>
  );
};

export default WorkForm;
